// src/context/AuthContext.jsx
// ─────────────────────────────────────────────────────────────────────────────
//  Global authentication state.
//
//  On first mount → calls GET /auth/me to check if the browser already has a
//  valid cookie (page refresh, new tab, etc.).  The rest of the app renders
//  ONLY after that check finishes, so protected routes never flash.
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getMeApi, loginApi, logoutApi } from "../api/auth.api";

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true); // bootstrap check
  const [authLoading, setAuthLoading] = useState(false); // login/logout action

  // ── Bootstrap: check existing session on every page load ─────────────────
  const bootstrap = useCallback(async () => {
    try {
      const { data } = await getMeApi();
      setUser(data.user);
    } catch {
      setUser(null); // no valid cookie → treat as logged out
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    bootstrap();
  }, [bootstrap]);

  // ── Login ─────────────────────────────────────────────────────────────────
  const login = async (email, password) => {
    setAuthLoading(true);
    try {
      // console.log(email, password);
      const { data } = await loginApi(email, password);
      setUser(data.user);
      return { success: true };
    } catch (err) {
      const message =
        err.response?.data?.message || "Login failed. Please try again.";
      return { success: false, message };
    } finally {
      setAuthLoading(false);
    }
  };

  // ── Logout ────────────────────────────────────────────────────────────────
  const logout = async () => {
    setAuthLoading(true);
    try {
      await logoutApi();
    } catch {
      // Even if the request fails, clear local state
    } finally {
      setUser(null);
      setAuthLoading(false);
    }
  };

  const hasPermission = useCallback(
    (code) => {
      if (!user) return false;
      if (user.role === "admin") return true;
      return Array.isArray(user.permissions) && user.permissions.includes(code);
    },
    [user],
  );

  const hasAnyPermission = useCallback(
    (codes) => {
      if (!user) return false;
      if (user.role === "admin") return true;
      if (!Array.isArray(user.permissions) || !Array.isArray(codes)) return false;
      return codes.some((c) => user.permissions.includes(c));
    },
    [user],
  );

  const value = {
    user,
    loading,
    authLoading,
    login,
    logout,
    isAdmin: user?.role === "admin",
    isAuthenticated: !!user,
    hasPermission,
    hasAnyPermission,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook — clean API: const { user, login, logout } = useAuth()
export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used inside <AuthProvider>");
  return ctx;
};
